import { isEmpty } from 'lodash';
import { getThemeEnabled, hasThemeAccess } from 'rest/dist/gen/api/shops/shops';
import { CART_SAVINGS_LABEL_DEFAULTS, PDP_POSITION_DEFAULT } from 'config/defaultWidgetSettings';
import { withUseThemeColors } from 'utils/widgetThemeColors';

export const initialProductCountdownSettingsLabels = {
  containsProductTitle: true,
  afterProductTitleText: 'SALE ENDS IN',
  beforeProductTitleText: '',
  footerText: '',
  daysLabel: 'Days',
  hoursLabel: 'Hours',
  minutesLabel: 'Minutes',
  secondsLabel: 'Seconds',
};

export const initialProductCountdownSettingsColors = {
  textColor: '#333333',
  digitColor: '#FF5F5B',
  digitBackgroundColor: '#f8f4f1',
  progressBarColor: '#FF5F5B',
  progressBarBorderColor: '#808080',
};

export const initialProductCountdownSettings = {
  IsActive: false,
  // Storefront derives the palette from the theme while this is on (utils/widgetThemeColors.js).
  UseThemeColors: true,
  ...initialProductCountdownSettingsLabels,
  ...initialProductCountdownSettingsColors,
};

const initialProductEmbedSetting = {
  IsActive: false,
  // Storefront derives the palette from the theme while this is on (utils/widgetThemeColors.js).
  UseThemeColors: true,

  Buy: 'Buy',
  get: 'get',
  off: 'off',
  items: 'items',
  Purchase: 'purchase',
  to: 'to',
  off_by_purchasing: 'off by purchasing',
  Save: 'save',
  bypurchasing: 'by purchasing',
  Buy_at_least: 'Buy at least',

  total: 'Total',
  peritem: 'per item',
  oneach: 'on each',

  // not show in preview
  between: 'between',
  and: 'and',
  discountonyourtotalpurchase: 'Discount on your total purchase',

  header_color: '#000000',
  description_color: '#898f94',
  badge_color: '#FF0101',
  border_color: '#898f94',
  border_color_selected: '#8CB540',
  radio_color: '#8CB540',
};

const initialCartEmbedSetting = {
  IsActive: false,
  // Storefront derives the palette from the theme while this is on (utils/widgetThemeColors.js).
  UseThemeColors: true,
  totalLabel: 'Subtotal',
  ...CART_SAVINGS_LABEL_DEFAULTS,
  totalColor: '#000000',
  savedColor: '#111111',
  savedBgColor: '#F5F5F5',
  align: 'default',
  lineThrough: 'true',
  boarderinSavedColor: '#DDDDDD',
  borderRadius: 8,
  showBorder: true,
};

const initialInventoryEmbedSetting = {
  IsActive: false,
  // Storefront derives the palette from the theme while this is on (utils/widgetThemeColors.js).
  UseThemeColors: true,
  Position: 'below',
  StockLessNumber: 5,
  StockMinNumber: 1,
  StockMinEnabled: false,
  Text: '🔥 Only {live_stock_count} in stock',
  TextColor: '#8E1F0B',
  BackgroundColor: '#FEDAD9',
};

const initialShippingEmbedSetting = {
  IsActive: false,
  // Storefront derives the palette from the theme while this is on (utils/widgetThemeColors.js).
  UseThemeColors: true,
  showOnProductPage: false,
  showOnCartPage: true,
  progressMessage: 'Buy {quantity/amount} more to get {discount}',
  initialMessage: 'Add {{amount}} more to unlock {{discount}} off shipping!',
  inProgressMessage: "You're {{amount}} away from {{discount}} off shipping!",
  thresholdReachedMessage: "You've unlocked {{discount}} off shipping!",
  // Card styles
  cardTextSize: 12,
  textColor: '#000000',
  backgroundColor: '#FFFFFF',
  cardBorderColor: '#000000',
  cardBorderRadius: 8,
  // Progress bar styles
  progressBackground: '#D9D9D9',
  progressColor: '#19A07F',
  progressBarBorderRadius: 4,
};

const initialBundleEmbedSetting = {
  // BUG-54b: storefront (DiscountPrime.liquid) treats a shop with NO saved bundle settings as ON (only an explicit
  // IsActive=false hides it), so the admin must show ON too; shops saved before the widget existed otherwise
  // read "off" here while the banner/cards still render.
  IsActive: true,
  PdpPosition: PDP_POSITION_DEFAULT,
  // Storefront derives the palette from the theme while this is on; the colours below
  // apply once a merchant turns it off (contract: utils/widgetThemeColors.js).
  UseThemeColors: true,

  WidgetBackgroundColor: '#F7F7F7',
  HeaderBackgroundColor: '#CDFEE1',
  TitleColor: '#303030',
  SubTitleColor: '#616161',
  TextColor: '#303030',

  GiftBorderColor: '#A8D8FF',
  GiftBackgroundColor: '#EAF4FF',
};

const initialBogoFabSetting = {
  IsActive: false,
  // Storefront derives the palette from the theme while this is on (utils/widgetThemeColors.js).
  UseThemeColors: true,
  removeCloseButton: false,
  position: 'left',
  borderRadius: 16,
  fabBackgroundColor: '#03A87C',
  fabIconColor: '#FFFFFF',
  closeButtonColor: '#FFFFFF',
  closeButtonIconColor: '#C0C0C0',
};

const initialBogoModalSetting = {
  IsActive: false,
  // Storefront derives the palette from the theme while this is on (utils/widgetThemeColors.js).
  UseThemeColors: true,
  // Text customization — Header texts
  headerTitle: 'Your current cart qualifies for these rewards',
  headerSubtitle: 'Now you are able to choose between them. Add before checking out!',
  // Text customization — Button labels
  selectButtonText: 'Select',
  addToCartButtonText: 'Add to Cart',
  continueShoppingButtonText: 'Continue Shopping',
  removeContinueShopping: false,
  // Discount badge
  badgeType: 'percentage', // 'percentage' | 'amount'
  showFreeGiftBadge: true,
  // Color palette — Theme colors
  primaryColor: '#000000',
  secondaryColor: '#FFFFFF',
  borderColor: '#C0C0C0',
  // Color palette — Element colors
  popupBackgroundColor: '#F4F4F4',
  headerBackgroundColor: '#616161',
  headerTextColor: '#FFFFFF',
  saleBadgeColor: '#3752B2',
  // Advanced
  dismissBehavior: 'dismiss_until_triggered', // 'dismiss_until_triggered' | 'dismiss_2min' | 'keep_showing'
};

const initialSalesBadgeSettings = {
  IsActive: true,
  // Storefront derives the palette from the theme while this is on (utils/widgetThemeColors.js).
  UseThemeColors: true,
  salesBadgeText: 'Special Offer',
  salesBadgeTextColor: '#FF5F5B',
  salesBadgeBackgroundColor: '#f8f4f1',
  salesBadgeType: 'circle',
  salesBadgePosition: 'top_left',
  salesBadgeHasBorder: false,
};

// Free Gift widget settings (shop metafield `prime.product-Gift-embed-settings`,
// read by theme-app-extension assets/gift-widget.js). Every string and colour
// below is the exact default the widget itself falls back to (its T(key, default)
// calls and the --gw-* CSS variable defaults), so an unsaved page and the storefront agree.
export const GIFT_TEXT_KEYS = [
  'DefaultTitle',
  'ProgressText',
  'UnlockedText',
  'ChoiceGiftText',
  'PickedCountText',
  'FreeText',
  'ChooseText',
  'RemoveText',
  'UnlockedToastText',
  'ChoiceUnlockedToastText',
  'UndoText',
  'AllUnlockedText',
  'GiftsHeadingText',
  'AddThisToUnlockText',
  'SomeAddedText',
  'AddBackText',
];
export const GIFT_COLOR_KEYS = [
  'WidgetBackgroundColor',
  'TextColor',
  'TitleColor',
  'ProgressBarColor',
  'ProgressBackgroundColor',
  'ButtonBackgroundColor',
  'ButtonTextColor',
];
export const initialGiftEmbedSetting = {
  IsActive: true,
  PdpPosition: PDP_POSITION_DEFAULT,
  UseThemeColors: true,

  DefaultTitle: 'Your free gift',
  ProgressText: '{amount} more to unlock {gift}',
  UnlockedText: 'You unlocked {gift}!',
  ChoiceGiftText: 'a free gift of your choice',
  PickedCountText: '{picked} of {total} chosen',
  FreeText: 'Free',
  ChooseText: 'Choose',
  RemoveText: 'Remove',
  UnlockedToastText: 'Free gift unlocked: {gift}',
  ChoiceUnlockedToastText: 'Choose your free gift!',
  UndoText: 'Undo',
  AllUnlockedText: 'All {count} free gifts unlocked!',
  GiftsHeadingText: 'Your free gifts',
  AddThisToUnlockText: 'Add this item to unlock it',
  SomeAddedText: '{added} of {total} free gifts added',
  AddBackText: 'Add {gift}',

  WidgetBackgroundColor: '#FFFFFF',
  TextColor: '#222222',
  TitleColor: '#111111',
  ProgressBarColor: '#111111',
  ProgressBackgroundColor: '#E6E6E6',
  ButtonBackgroundColor: '#111111',
  ButtonTextColor: '#FFFFFF',
};

const initialCrossSellEmbedSetting = {
  // BUG-54b: storefront (DiscountPrime.liquid) treats a shop with NO saved cross-sell settings as ON (only an explicit
  // IsActive=false hides it), so the admin must show ON too; shops saved before the widget existed otherwise
  // read "off" here while the banner/cards still render.
  IsActive: true,
  PdpPosition: PDP_POSITION_DEFAULT,
  UseThemeColors: true,

  // Same strings the storefront widget (xsell-widget.js) falls back to.
  GeneralTitle: 'Frequently bought together',
  CartTitle: 'You might also like',
  AddToCardButton: 'Add',
  AddedText: 'Added',
  OffText: 'off',
  NewPriceText: 'New price',
  RetryText: 'Please try again',

  WidgetBackgroundColor: '#F7F7F7',
  TextColor: '#303030',
  ButtonBackgroundColor: '#29845A',
  ButtonTextColor: '#ffffff',
};

// Upsell widget settings (upsell plan §2.6: `prime.product-Upsell-embed-settings`
// mirrors the Cross-Sell settings — IsActive, colours, per-language
// phrasing/upgradeButton/undo texts (other languages under `translations`,
// see WidgetLanguageTabs), undoSeconds). The English default phrasing is the
// builder's DEFAULT_PHRASING; UpsellEmbedSetting.js swaps in the translated
// default for the active locale on "Reset".
const initialUpsellEmbedSetting = {
  // BUG-54b: storefront (DiscountPrime.liquid) treats a shop with NO saved upsell settings as ON (only an explicit
  // IsActive=false hides it), so the admin must show ON too; shops saved before the widget existed otherwise
  // read "off" here while the banner/cards still render.
  IsActive: true,
  PdpPosition: PDP_POSITION_DEFAULT,
  UseThemeColors: true,

  phrasing: 'Upgrade to {title} - only {diff} more',
  noExtraCostPhrasing: 'Upgrade to {title} at no extra cost',
  upgradeButton: 'Upgrade',
  switchButton: 'Switch',
  swapped: 'Upgraded to {title}',
  retry: 'Please try again',
  dismiss: 'Dismiss',
  undo: 'Undo',
  cartPhrasing: 'Upgrade your {from} to {to} for {amount} more',
  cartNoExtraCostPhrasing: 'Upgrade your {from} to {to} at no extra cost',
  pdpPhrasing: 'Get {title} for only {diff} more',
  pdpNoExtraCostPhrasing: 'Get {title} at no extra cost',
  pdpAddButton: 'Add upgrade to cart',
  pdpAddedText: '{title} is in your cart',
  undoSeconds: 10,

  WidgetBackgroundColor: '#F7F7F7',
  TextColor: '#303030',
  ButtonBackgroundColor: '#29845A',
  ButtonTextColor: '#ffffff',
};
// useThemeExtensionActivation() (hooks/useActivateThemeExtension.js) is used
// by 13+ components across campaign forms, banners, previews and analytics
// empty states — with no sharing, every one of them mounting at once (e.g.
// several on the same dashboard render) fired its OWN independent
// getThemeEnabled/hasThemeAccess request. Measured on a real trace: 6 separate
// /api/shops/themeEnabled calls and 3 separate /api/shops/has_theme_access
// calls in one page load, several taking 500-1000ms each. Same shared-promise
// pattern already used for the App Bridge session token
// (rest/src/utils/fetchInstance.ts) and dashboard bootstrap.
//
// Window is deliberately short — long enough to dedupe several components
// mounting in the same render burst (a few ms apart), but short enough that a
// caller doing a genuine manual refresh() shortly after an action (e.g. right
// after activating the embed) still gets a fresh answer, not a stale cached
// one. api/index.js's separate cache for the SAME 2 endpoints (a different
// call path, used by useAppEmbedStatus/Onboarding) uses the same window for
// the same reason — that one's comment has the fuller story (an explicit
// 1500ms-spaced retry loop that a longer window would have silently fed its
// own stale result).
let themeActivationPromise;
let themeActivationFetchedAt = 0;
let themeScopePromise;
let themeScopeFetchedAt = 0;
const THEME_STATUS_REUSE_WINDOW_MS = 300;

export async function getThemeExtentionActivation() {
  const now = Date.now();
  if (themeActivationPromise && now - themeActivationFetchedAt < THEME_STATUS_REUSE_WINDOW_MS) {
    return themeActivationPromise;
  }
  themeActivationFetchedAt = now;
  themeActivationPromise = getThemeEnabled()
    .then((response) => response.data == 'false' ? true : false)
    .catch((err) => {
      themeActivationPromise = undefined;
      throw err;
    });
  return themeActivationPromise;
}
export async function hastThemeScope() {
  const now = Date.now();
  if (themeScopePromise && now - themeScopeFetchedAt < THEME_STATUS_REUSE_WINDOW_MS) {
    return themeScopePromise;
  }
  themeScopeFetchedAt = now;
  themeScopePromise = hasThemeAccess().catch((err) => {
    themeScopePromise = undefined;
    throw err;
  });
  return themeScopePromise;
}
export function getShopInfoAdaptor(shopInfo, onboard) {
  // Defensive: a freshly installed shop may not have its plan assigned yet (or
  // a backend hiccup). Default currentPlan so the permissions mapping below
  // doesn't crash the whole app with "Cannot read properties of undefined".
  if (shopInfo && !shopInfo.currentPlan) {
    shopInfo.currentPlan = { permissions: [] };
  }
  var onboardingData = false;
  var filteredOnboard = onboard?.filter((onboard) => onboard.key == 'onboardingFirstCampaign');
  if (filteredOnboard.length > 0) {
    onboardingData = filteredOnboard[0].value;
  }
  var onboardingActivateCampaignData = false;
  var filteredOnboardingActivateCampaign = onboard.filter(
    (onboard) => onboard.key == 'onboardingActivateCampaign'
  );
  if (filteredOnboardingActivateCampaign.length > 0) {
    onboardingActivateCampaignData = filteredOnboardingActivateCampaign[0].value;
  }
  var onboardingNeedUpgradeCampaignData = false;
  var filteredOnboardingNeedUpgradeCampaign = onboard.filter(
    (onboard) => onboard.key == 'onboardingNeedUpgradeCampaign'
  );
  if (filteredOnboardingNeedUpgradeCampaign.length > 0) {
    onboardingNeedUpgradeCampaignData = filteredOnboardingNeedUpgradeCampaign[0].value;
  }
  var onboardingDismissData = false;
  var filteredOnboardingDismiss = onboard.filter((onboard) => onboard.key == 'onboardingDismiss');
  if (filteredOnboardingDismiss.length > 0) {
    onboardingDismissData = filteredOnboardingDismiss[0].value;
  }
  var onboardingThemeExtensionData = false;
  var filteredOnboardingThemeExtension = onboard.filter(
    (onboard) => onboard.key == 'onboardingThemeExtension'
  );
  if (filteredOnboardingThemeExtension.length > 0) {
    onboardingThemeExtensionData = filteredOnboardingThemeExtension[0].value;
  }
  var onboardingThemeExtension2Data = false;
  var filteredOnboardingThemeExtension2 = onboard.filter(
    (onboard) => onboard.key == 'onboardingThemeExtension2'
  );
  if (filteredOnboardingThemeExtension2.length > 0) {
    onboardingThemeExtension2Data = filteredOnboardingThemeExtension2[0].value;
  }
  // video dismiss
  var onboardingVideoDismissData = false;
  var filteredOnboardingThemeExtension2videodismiss = onboard.filter(
    (onboard) => onboard.key == 'onboardingVideo'
  );

  if (filteredOnboardingThemeExtension2videodismiss.length > 0) {
    onboardingVideoDismissData = filteredOnboardingThemeExtension2videodismiss[0].value;
  }
  //endvideodismis

  // onboardingFinalEnableCheck
  let onboardingFinalEnableCheck = false;
  let filteredOnboardingFinalEnableCheck = onboard.filter(
    (onboard) => onboard.key == 'onboardingFinalEnableCheck'
  );
  if (filteredOnboardingFinalEnableCheck.length > 0) {
    onboardingFinalEnableCheck = filteredOnboardingFinalEnableCheck[0].value;
  }
  // Static feature flag for UI (later connect to API)
  const embeddedSetting = false;

  //later
  // const embeddedSetting = Boolean(shopInfo?.embeddedSetting);

  return {
    id: shopInfo?.id,
    customer_email: shopInfo.ownerEmail,
    domain: shopInfo.shopifyDomain,
    shop_owner: shopInfo.ownerName,
    ownerEmail: shopInfo.ownerEmail,
    name: shopInfo.name,
    phone: shopInfo.ownerPhoneNumber,
    shopify_domain: shopInfo.shopifyDomain,
    contact_email: shopInfo.ownerEmail,
    myshopify_domain: shopInfo.shopifyDomain,
    money_format: shopInfo.moneyFormat,
    money_with_currency_format: shopInfo.moneyWithCurrencyFormat,
    iana_timezone: shopInfo.ianaTimezone,
    timezone: shopInfo.timezone,
    currency: shopInfo.currency,
    shopify_plan: shopInfo.planName,
    country: shopInfo.country,
    isTest: shopInfo.isTest,
    created_at: shopInfo.createdAt,
    primary_locale: shopInfo.primaryLocale,
    shop_locales: shopInfo.shopLocales || [],
    currentPlan: {
      currentPlanId: shopInfo.currentPlan?.id,
      currentPlanPeriod: shopInfo.currentPlanPeriod === 'monthly' ? 0 : 1,
      CurrentPlanUser: '',
      planName: shopInfo.currentPlan?.name,
    },
    embeddedSetting,
    // TODO: fake data
    trialEligible: true,
    permissions: {
      can_set_schedule: shopInfo.currentPlan.permissions.includes('campaign_start_schedule'),
      can_set_exclude_product: shopInfo.currentPlan.permissions.includes(
        'campaign_exclude_products'
      ),
      can_set_discount_type: shopInfo.currentPlan.permissions.includes('campaign_start_schedule'),
      can_set_onFly_campaign: true,
      campaign_quantity_and_volume_discount_theme_extension:
        shopInfo.currentPlan.permissions.includes(
          'campaign_quantity_and_volume_discount_theme_extension'
        ),
      can_set_General_Custom_Badge: shopInfo.currentPlan.permissions.includes(
        'can_set_General_Custom_Badge'
      ),
      // Custom wholesale Discount progress bar (Premium and higher). Without it the
      // merchant is limited to the global default widget.
      can_set_custom_progress_bar: shopInfo.currentPlan.permissions.includes(
        'can_set_custom_progress_bar'
      ),
    },
    canAddCampaign: true,
    onboardingFirstCampaignPassed: onboardingData,
    onboardingActivateCampaignPassed: onboardingActivateCampaignData,
    onboardingNeedUpgradeCampaign: onboardingNeedUpgradeCampaignData,
    onboardingThemeExtension: onboardingThemeExtensionData,
    onboardingThemeExtension2: onboardingThemeExtension2Data,
    onboardingVideo: onboardingVideoDismissData,
    onboardingFinalEnableCheck: onboardingFinalEnableCheck,
    onboardingDismiss: onboardingDismissData,
    onboardingFirstCampaign: onboardingData,
    onboardingActivateCampaign: onboardingActivateCampaignData,
    weeklyDigestEnabled: shopInfo.weeklyDigestEnabled ?? false,
    weeklyDigestEmail: shopInfo.weeklyDigestEmail ?? null,
    functionOrderNote: shopInfo.functionOrderNote,
    functionProductNote: shopInfo.functionProductNote,
    productCountdownSettings: isEmpty(shopInfo.productCountdownSettings)
      ? initialProductCountdownSettings
      : withUseThemeColors('countdown', shopInfo.productCountdownSettings),
    productEmbedSettings: isEmpty(shopInfo.productEmbedSettings)
      ? initialProductEmbedSetting
      : withUseThemeColors('product', shopInfo.productEmbedSettings),
    cartEmbedSettings: isEmpty(shopInfo.cartEmbedSettings)
      ? initialCartEmbedSetting
      : withUseThemeColors('cart', shopInfo.cartEmbedSettings),
    inventoryEmbedSettings: isEmpty(shopInfo.InventoryEmbedSettings)
      ? initialInventoryEmbedSetting
      : withUseThemeColors('inventory', shopInfo.InventoryEmbedSettings),
    shippingEmbedSettings: isEmpty(shopInfo.shippingEmbedSettings)
      ? initialShippingEmbedSetting
      : { ...initialShippingEmbedSetting, ...withUseThemeColors('shipping', shopInfo.shippingEmbedSettings) },
    // Bundle / cross-sell / upsell / gift: `UseThemeColors` is made explicit from the RAW
    // saved object (old-default detection for shops saved before the flag existed) BEFORE
    // any merge with the defaults, so a merged default never masks a customised palette.
    bundleEmbedSettings: isEmpty(shopInfo.bundleEmbedSettings)
      ? initialBundleEmbedSetting
      : withUseThemeColors('bundle', shopInfo.bundleEmbedSettings),
    crossSellEmbedSettings: isEmpty(shopInfo.CrossSellSettings)
      ? initialCrossSellEmbedSetting
      : withUseThemeColors('crossSell', shopInfo.CrossSellSettings),
    // Shop.UpsellSettings (PascalCase like CrossSellSettings, written by
    // POST /api/shops/UpsellSettings); merged over the defaults so a
    // partially-saved object never drops a field.
    upsellEmbedSettings: isEmpty(shopInfo.UpsellSettings)
      ? initialUpsellEmbedSetting
      : { ...initialUpsellEmbedSetting, ...withUseThemeColors('upsell', shopInfo.UpsellSettings) },
    // Shop.GiftSettings (written by POST /api/shops/GiftSettings); merged over the
    // defaults so a partially-saved object never drops a field.
    giftEmbedSettings: isEmpty(shopInfo.GiftSettings)
      ? initialGiftEmbedSetting
      : { ...initialGiftEmbedSetting, ...withUseThemeColors('gift', shopInfo.GiftSettings) },
    salesBadgeSettings: isEmpty(shopInfo.salesBadgeSettings)
      ? initialSalesBadgeSettings
      : { ...initialSalesBadgeSettings, ...withUseThemeColors('salesBadge', shopInfo.salesBadgeSettings) },
    bogoModalSettings: isEmpty(shopInfo.bogoModalSettings)
      ? initialBogoModalSetting
      : withUseThemeColors('bogoModal', shopInfo.bogoModalSettings),
    bogoFabSettings: isEmpty(shopInfo.bogoFabSettings)
      ? initialBogoFabSetting
      : withUseThemeColors('bogoFab', shopInfo.bogoFabSettings),
    notification: {
      theme_extension: {
        show: true,
        count: 0,
      },
    },
  };
}
